import { useMutation } from '@tanstack/react-query';
import { apiUserAccount } from '../../../utils/ApiService';
import { notification } from 'components/ds';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { handleApiError } from 'components/ErrorHandler';
import { SCHEMA_STORAGE_KEY } from './SchemaService';
import { ValidatorField } from 'components/ValidatorField';
import { ApiEndpoints } from '../constantes/ApiEndpoints';
import { getSchemaIdFromStorage } from '../../../utils/getSchemaIdFromStorage';

export interface AcceptedTermsDto {
  termId: string;
  fileId: string;
}

export interface GeolocationDto {
  latitude?: string;
  longitude?: string;
}

export interface UserAgentDto {
  browser?: string;
  browserVersion?: string;
  operationalSystem?: string;
  device?: string;
  deviceType?: string;
  language?: string;
}

export interface DeviceDataDto {
  ip: string;
  geolocation?: GeolocationDto;
  userAgent?: UserAgentDto;
}

export interface RegisterAccountRequest {
  schemaId: string;
  userData: any;
  terms: AcceptedTermsDto[];
  deviceData?: DeviceDataDto;
}

export interface UserContactData {
  email: string;
  phoneNumber: string;
}

export const usePostResetPassword = () => useMutation(async (data: { code: string, newPassword: string }) => {
  const response = await apiUserAccount.post('/reset-password', data) as any;
  
  return response.data;
}, {
  onSuccess: () => {
    notification.success({
      message: 'Senha redefinida',
      description: 'Sua senha foi redefinida com sucesso.'
    });
  },
  onError: (error) => handleApiError(error, 'Erro ao redefinir senha')
});

export const usePostSendResetCode = () => useMutation(async (email: string) => {
  const response = await apiUserAccount.post('/send-reset-code', {email}) as any;
  
  return response.data;
}, {
  onSuccess: () => {
    notification.success({
      message: 'Código enviado',
      description: 'Um código de segurança foi enviado para o seu e-mail.'
    });
  },
  onError: (error) => handleApiError(error, 'Erro ao enviar código')
});

export const usePostCheckEmailAvailability = () => useMutation(async (email: string) => {
  const response = await apiUserAccount.post('/check-email', {email}) as any;
  
  return response.data.exists;
}, {
  onError: (error) => handleApiError(error, 'Erro ao verificar e-mail')
});

export const usePostRegisterUser = () => useMutation(async (userData: Omit<RegisterAccountRequest, 'deviceData'>) => {
  
  const deviceData: DeviceDataDto = {
    ip: '',
    userAgent: {
      browser: navigator?.userAgent?.split(' ')[0] || '',
      browserVersion: navigator?.userAgent?.split(' ')[1] || '',
      operationalSystem: navigator?.platform || '',
      device: navigator?.userAgent || '',
      deviceType: /Mobile|Tablet|iPad|iPhone|Android/.test(navigator?.userAgent) ? 'mobile' : 'desktop',
      language: navigator?.language || ''
    },
    geolocation: {}
  };
  
  try {
    if (navigator.geolocation) {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: true
        });
      });
      
      deviceData.geolocation = {
        latitude: position.coords.latitude.toString(),
        longitude: position.coords.longitude.toString()
      };
    }
  } catch (error) {
    deviceData.geolocation = {};
  }
  
  const schema = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
  
  const requestData: RegisterAccountRequest = {
    schemaId: schema.id,
    terms: schema.terms?.map((term: any) => ({
      termId: term.id,
      fileId: term.fileId
    })),
    userData: {
      ...userData,
      phoneNumber: ValidatorField.extractNumbers((userData as any).phoneNumber)
    },
    deviceData
  };
  
  const schemaId = getSchemaIdFromStorage();
  const response = await apiUserAccount.post(`${ApiEndpoints.account.register}?schemaId=${schemaId}`, requestData) as any;
  
  return response.data || response;
}, {
  onSuccess: (data) => {
    ApiLocalStorage.set('register', data);
    
    notification.success({
      message: 'Registro bem-sucedido',
      description: 'Seu registro foi realizado com sucesso.'
    });
  },
  onError: (error: any) => {
    handleApiError(error, 'Erro ao criar conta');
  }
});

export const useGetUserByUsername = () => useMutation(
  async (username: string) => {
    const schemaId = getSchemaIdFromStorage();
    const response = await apiUserAccount.get<any>(`${ApiEndpoints.auth.userSearch}/${ValidatorField.removeSpecialCharsFromUsername(username)}?schemaId=${schemaId}`);
    
    return {
      email: response.fields.email,
      phoneNumber: response.fields.cellphone || response.fields.phoneNumber,
      participantId: response.fields.participantId
    } as UserContactData;
  },
  {
    onError: (error) => handleApiError(error, 'Erro ao buscar dados')
  }
);
